import { useState } from 'react';
import type { RfwScoreData, ScoreTrend } from '@/mocks/rfwScores';
import { trendConfig } from '@/mocks/rfwScores';
import { formatScoreCompact, formatScoreChange, getRankingChange } from '@/utils/rfwScoreCalculator';
import ScoreBreakdownModal from './ScoreBreakdownModal';

interface RfwScoreCardProps {
  score: RfwScoreData;
  entityLabel: string;
  variant?: 'compact' | 'full';
}

export default function RfwScoreCard({ score, entityLabel, variant = 'full' }: RfwScoreCardProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const trend = trendConfig[score.trend];
  const rankingChange = getRankingChange(score.rankingPosition, score.rankingPreviousPosition);

  if (variant === 'compact') {
    return (
      <>
        <button
          onClick={() => setShowBreakdown(true)}
          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-colors cursor-pointer text-left w-full"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">RFW Score</span>
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${trend.bg} ${trend.color}`}>
                {trend.emoji}
              </span>
            </div>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-xl font-bold text-foreground-950">{formatScoreCompact(score.currentScore)}</span>
              <span className={`text-xs font-semibold ${score.scoreChange > 0 ? 'text-emerald-600' : score.scoreChange < 0 ? 'text-red-500' : 'text-foreground-400'}`}>
                {score.scoreChange > 0 ? '+' : ''}{formatScoreCompact(score.scoreChange)}
              </span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="flex items-center gap-1 justify-end">
              <span className="text-lg font-bold text-foreground-950">#{score.rankingPosition}</span>
              {rankingChange.direction !== 'same' && (
                <span className={`text-xs font-semibold ${rankingChange.direction === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                  {rankingChange.direction === 'up' ? '↑' : '↓'}{rankingChange.delta}
                </span>
              )}
            </div>
            <span className="text-[10px] text-foreground-400">{entityLabel}</span>
          </div>
        </button>

        <ScoreBreakdownModal
          open={showBreakdown}
          onClose={() => setShowBreakdown(false)}
          score={score}
          entityLabel={entityLabel}
        />
      </>
    );
  }

  return (
    <>
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-background-200/70">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-flashlight-fill text-accent-600 text-sm"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-950">RFW Score</h3>
                <p className="text-[10px] text-foreground-400">{entityLabel}</p>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${trend.bg} ${trend.color}`}>
              <i className={`${trend.icon} text-xs`}></i>
              {trend.emoji} {trend.label}
            </span>
          </div>
        </div>

        {/* Score Value */}
        <div className="px-5 py-5">
          <div className="flex items-end justify-between">
            <div>
              <span className="text-3xl font-extrabold text-foreground-950 leading-none">
                {score.currentScore.toLocaleString()}
              </span>
              <p className={`text-sm font-semibold mt-1 ${score.scoreChange > 0 ? 'text-emerald-600' : score.scoreChange < 0 ? 'text-red-500' : 'text-foreground-400'}`}>
                {formatScoreChange(score.scoreChange)}
              </p>
            </div>

            {/* Ranking */}
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <span className="text-xs text-foreground-400">Ranking</span>
                {rankingChange.direction !== 'same' && (
                  <span className={`flex items-center gap-0.5 text-xs font-bold ${rankingChange.direction === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                    <i className={`${rankingChange.direction === 'up' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
                    {rankingChange.delta}
                  </span>
                )}
              </div>
              <span className="text-3xl font-extrabold text-foreground-950">#{score.rankingPosition}</span>
            </div>
          </div>

          {/* Weekly & Monthly */}
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-background-200/70">
            <div className="text-center">
              <span className="text-lg font-bold text-foreground-950">{formatScoreCompact(score.weeklyScore)}</span>
              <p className="text-[10px] text-foreground-400">Esta semana</p>
            </div>
            <div className="text-center">
              <span className="text-lg font-bold text-foreground-950">{formatScoreCompact(score.monthlyScore)}</span>
              <p className="text-[10px] text-foreground-400">Este mes</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-background-50/50 border-t border-background-200/70">
          <button
            onClick={() => setShowBreakdown(true)}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-background-100 hover:bg-background-200/70 text-sm font-semibold text-foreground-700 transition-colors cursor-pointer"
          >
            <i className="ri-bar-chart-line text-base"></i>
            Ver Detalles
          </button>
        </div>
      </div>

      <ScoreBreakdownModal
        open={showBreakdown}
        onClose={() => setShowBreakdown(false)}
        score={score}
        entityLabel={entityLabel}
      />
    </>
  );
}